import { Component, Inject, OnInit } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { I18nService, StaticService } from '@app/i18n/services';
import { FeedbackService } from '@services/feedback';
import { FormsService } from '@services/forms';
import { ILegalUploader, LegalsService, TermType } from '@services/legals';
import { ProgressIndicatorService } from '@services/progress';
import { FormValidators } from '@tcp13equals2/z5-lib';
import { of } from 'rxjs';
import { catchError, concatMap, finalize, map } from 'rxjs/operators';

import { FormEditorInputType } from '../../../../../../shared/components';
import { NewLegal } from './legal-new.models';

enum FileTypes {
	url = 'url',
	upload = 'upload'
}

@Component({
	selector: 'app-legal-new',
	templateUrl: './legal-new.dialog.html',
	styleUrls: ['./legal-new.dialog.scss']
})
export class LegalNewDialogComponent implements OnInit {
	public formInputs = this.formBuilder.group({
		name: [null, [Validators.required]],
		alias: [null],
		url: [null, [], [this.formsService.urlValid()]],
		locale: [null, [Validators.required]],
		version: [null, [FormValidators.required]],
		active: [null, [Validators.required]],
		cluster: [''],
		fileType: [FileTypes.url],
		localisation: this.formBuilder.group({})
	});

	public fileTypes = FileTypes;
	public termType = TermType;
	public formEditorInputType = FormEditorInputType;
	public locales: {
		value: string;
		label: string;
	}[] = [];
	public statuses = [
		{ value: true, label: this.i18nService.get('publish', 'app.descriptor.publish') },
		{ value: false, label: this.i18nService.get('draft', 'app.descriptor.draft') }
	];
	public maxSize = 30000000; // 30MB

	get formIsValid(): boolean {
		return this.formInputs.valid;
	}

	constructor(
		private legalsService: LegalsService,
		private staticService: StaticService,
		private i18nService: I18nService,
		private progressIndicatorService: ProgressIndicatorService,
		private feedbackService: FeedbackService,
		private formsService: FormsService,
		@Inject(MAT_DIALOG_DATA) public dialogData: NewLegal,
		private dialogRef: MatDialogRef<LegalNewDialogComponent>,
		private formBuilder: FormBuilder
	) {}

	public ngOnInit(): void {
		this.locales = this.dialogData?.locales
			? this.dialogData.locales
			: this.staticService.get('locales').map((locale) => {
					return {
						value: locale.id,
						label: locale.label
					};
			  });

		this.formInputs.patchValue({
			name: this.dialogData.legal?.name ? this.dialogData.legal.name : '',
			alias: this.dialogData.legal?.alias ? this.dialogData.legal.alias : '',
			url: this.dialogData.legal?.url ? this.dialogData.legal.url : '',
			locale: this.dialogData.legal?.locale ? this.dialogData.legal.locale : 'en',
			version: this.dialogData.legal?.version ? this.dialogData.legal.version : '1.0',
			active: this.dialogData.legal?.active ? this.dialogData.legal.active : false,
			cluster: this.dialogData?.cluster?.selectedCluster?.clusterId ? this.dialogData.cluster.selectedCluster.clusterId : ''
		});

		if (this.dialogData.type !== TermType.app) {
			this.formInputs.get('version').disable();
		}

		this.formInputs.get('fileType').valueChanges.subscribe((value) => {
			this.formInputs.get('url').setValidators([Validators.required]);
			this.formInputs.get('url').updateValueAndValidity();
		});
	}

	public filename(file: any): string {
		if (file) {
			return file.name;
		}
		return '';
	}

	public filesize(file: any): string {
		if (file) {
			const size = file.size;
			if (size < 1000) {
				return Math.round(size) + 'b';
			} else if (size < 1000000) {
				return Math.round(size / 100) / 10 + 'Kb';
			} else if (size < 1000000000) {
				return Math.round(size / 100000) / 10 + 'MB';
			}
		}
		return '';
	}

	public cancel(): void {
		if (this.dialogData.callback) {
			this.dialogData.callback(false);
		}
	}

	public onFormSubmit(): void {
		this.progressIndicatorService.spinner(true);

		const legal: ILegalUploader = {
			...this.dialogData.legal,
			name: this.formInputs.value.name,
			locale: this.formInputs.value.locale,
			version: this.formInputs.value.version,
			active: this.formInputs.value.active,
			alias: this.formInputs.value.alias
		};

		legal.url = this.formInputs.value.url;

		let addLegal = this.legalsService.addLegalDocument(
			legal,
			this.dialogData.type,
			this.formInputs.get('cluster').value
		);

		if (this.dialogData.type === this.termType.app) {
			const displayNames = {};

			for (const [key, value] of Object.entries(this.formInputs.value.localisation)) {
				if (value['displayName']) displayNames[key] = value['displayName'];
			}

			if (Object.keys(displayNames).length > 0) {
				addLegal = addLegal.pipe(
					concatMap((res) =>
						this.legalsService.localiseTerms(res.app.termsId, { displayNames }).pipe(
							map(() => res),
							catchError((error) => {
								this.feedbackService.apiError(error);
								return of(res);
							})
						)
					)
				);
			}
		}

		addLegal.pipe(finalize(() => this.progressIndicatorService.spinner(false))).subscribe(
			() => {
				if (this.dialogData.callback) this.dialogData.callback(true);
				this.dialogRef.close();
			},
			(error: any) => {
				if (this.dialogData.callback) this.dialogData.callback(false);
				this.feedbackService.apiError(error);
			}
		);
	}
}
